import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { VatProvider } from './context/VatContext';
import { SocketProvider } from './context/SocketContext';
import { Header } from './components/home/Header';
import { Footer } from './components/home/Footer';
import ScrollRestoration from './components/ScrollRestoration';
import TrailingSlashNormalizer from './components/TrailingSlashNormalizer';
import { useTheme } from './hooks/use-theme';
import Home from './pages/Home';
import Products from './pages/Products';
import Accessories from './pages/Accessories';
import Televisions from './pages/Televisions';
import ProductDetail from './pages/ProductDetail';
import KcbOffers from './pages/KcbOffers';
import NewArrivals from './pages/NewArrivals';
import BestSellers from './pages/BestSellers';
import Offers from './pages/Offers';
import OfferDetail from './pages/OfferDetail';
import PolicyPage from './pages/PolicyPage';
import LoyaltyProgram from './pages/LoyaltyProgram';
import NotFound from './pages/NotFound';
import Faq from './pages/Faq';
import About from './pages/About';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import PaymentStatus from './pages/PaymentStatus';
import OrderConfirmation from './pages/OrderConfirmation';
import TrackOrder from './pages/TrackOrder';
import AccountLayout from './pages/AccountLayout';
import Profile from './pages/Profile';
import Addresses from './pages/Addresses';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Wishlist from './pages/Wishlist';
import SupportTickets from './pages/SupportTickets';
import TicketDetail from './pages/TicketDetail';
import LoyaltyWallet from './pages/LoyaltyWallet';
import BulkOrderPage from './pages/BulkOrderPage';
import BulkInquiries from './pages/BulkInquiries';
import BulkInquiryDetail from './pages/BulkInquiryDetail';
import Contact from './pages/Contact';
import StoreLocator from './pages/StoreLocator';
import Blog from './pages/Blog';
import BlogDetail from './pages/BlogDetail';
import ProtectedRoute from './components/ProtectedRoute';
import GuestRoute from './components/GuestRoute';
import Seo from './components/Seo';
import WhatsAppFloatingIcon from './components/WhatsAppFloatingIcon';
import { SamsungThemeSync } from './components/SamsungThemeSync';

function App() {
  const { theme } = useTheme();

  return (
    <BrowserRouter basename={__BASE_PATH__}>
      <AuthProvider>
        <VatProvider>
          <CartProvider>
            <WishlistProvider>
              <SocketProvider>
                <ScrollRestoration />
                <TrailingSlashNormalizer />
                <Seo />
                <Header />
                <ToastContainer
                  theme={theme}
                  position="top-right"
                  autoClose={3500}
                  hideProgressBar={false}
                  newestOnTop
                  closeOnClick
                  pauseOnHover
                  draggable
                  icon={false}
                  transition={Slide}
                  limit={4}
                />
                <WhatsAppFloatingIcon />
                <SamsungThemeSync />
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
                  <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/all_products" element={<Products />} />
                  {/* Legacy WP pagination URLs (/page/N) stay first-class pages */}
                  <Route path="/products/page/:pageNum" element={<Products />} />
                  <Route path="/all_products/page/:pageNum" element={<Products />} />
                  <Route path="/accessories" element={<Accessories />} />
                  <Route path="/televisions" element={<Televisions />} />
                  <Route path="/product-category/:categorySlug/page/:pageNum" element={<Products />} />
                  <Route path="/product-category/:categorySlug/:subcategorySlug/page/:pageNum" element={<Products />} />
                  <Route path="/product-category/:categorySlug/:subcategorySlug/:subsubcategorySlug/page/:pageNum" element={<Products />} />
                  <Route path="/product-category/:categorySlug/:subcategorySlug/:subsubcategorySlug" element={<Products />} />
                  <Route path="/product-category/:categorySlug/:subcategorySlug?" element={<Products />} />
                  <Route path="/product/:slug" element={<ProductDetail />} />
                  {/* Legacy WP compare page — serve a products listing, never a redirect */}
                  <Route path="/compare_products" element={<Products />} />
                  {/* /product-tag/* redirected in TrailingSlashNormalizer (+ Server 301) */}
                  <Route path="/ram/:ramValue" element={<Products />} />
                  <Route path="/storage/:storageValue" element={<Products />} />
                  <Route path="/camera/:cameraValue" element={<Products />} />
                  <Route path="/kcb-offers" element={<KcbOffers />} />
                  <Route path="/kcb_offers" element={<KcbOffers />} />
                  <Route path="/new-arrivals" element={<NewArrivals />} />
                  <Route path="/best-sellers" element={<BestSellers />} />
                  <Route path="/offers" element={<Offers />} />
                  <Route path="/special-offers" element={<Offers />} />
                  <Route path="/offers/:slug" element={<OfferDetail />} />
                  <Route path="/special-offers/:slug" element={<OfferDetail />} />
                  <Route path="/faq" element={<Faq />} />
                  <Route path="/help" element={<Navigate to="/faq/" replace />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  {/* Legacy WP contact URL — same page, no redirect */}
                  <Route path="/contact-us" element={<Contact />} />
                  <Route path="/store-locator" element={<StoreLocator />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogDetail />} />

                  {/* Auth routes (guest only) */}
                  <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
                  <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
                  <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Shopping routes (public) */}
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/payment-status/:orderId" element={<PaymentStatus />} />
                  <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmation />} />
                  <Route path="/track-order" element={<TrackOrder />} />
                  <Route path="/track-your-order" element={<TrackOrder />} />
                  <Route path="/track-order/:token" element={<TrackOrder />} />
                  <Route path="/track-your-order/:token" element={<TrackOrder />} />

                  {/* Account routes (protected) */}
                  <Route path="/account" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}>
                    <Route index element={<Navigate to="/account/profile/" replace />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="addresses" element={<Addresses />} />
                    <Route path="orders" element={<Orders />} />
                    <Route path="orders/:orderNumber" element={<OrderDetail />} />
                    <Route path="wishlist" element={<Wishlist />} />
                    <Route path="loyalty" element={<LoyaltyWallet />} />
                    <Route path="support" element={<SupportTickets />} />
                    <Route path="support/:ticketNumber" element={<TicketDetail />} />
                    <Route path="bulk-inquiries" element={<BulkInquiries />} />
                    <Route path="bulk-inquiries/:inquiryNumber" element={<BulkInquiryDetail />} />
                  </Route>

                  <Route path="/loyalty-program" element={<LoyaltyProgram />} />
                  <Route path="/bulk-orders" element={<BulkOrderPage />} />
                  <Route path="/bulk-orders/accept-quote" element={<BulkOrderPage />} />
                  <Route path="/policy/:slug" element={<PolicyPage />} />
                  {/* Legacy WP wishlist URL — render the page here, no redirect */}
                  <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
                  {/* Root-level policy resolver — live WP privacy URL is /privacy-policy-2/ */}
                  <Route path="/privacy-policy" element={<Navigate to="/privacy-policy-2/" replace />} />
                  <Route path="/privacy-policy/" element={<Navigate to="/privacy-policy-2/" replace />} />
                  <Route path="/:slug" element={<PolicyPage />} />
                  <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <Footer />
              </SocketProvider>
            </WishlistProvider>
          </CartProvider>
        </VatProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
